import { Button, cn, Popover, PopoverContent, PopoverTrigger } from '@heroui/react'
import { MediaType } from '@qse/api'
import { useEffect, useState } from 'react'

import { FileTypeIcon } from '../file-type'

const Dropdown = ({
  title,
  options,
  startContent,
  endContent,
  hideArrow = false,
  hideStartContentItem = false,
  popoverContentClassName,
  placement = 'bottom-start',
  defaultSelectedOptionIndex,
  selectedOption: externalSelectedOption,
  onOptionChange,
  size = 'sm',
  showHighlight = false,
  classNameBtn = 'dark:bg-[#343537] bg-white text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-0',
}: {
  title: string
  options: {
    label: string
    mediaType?: MediaType
    isDir?: boolean
    mimeType?: string
    onSelect: () => void
    separator?: boolean
    icon?: React.ReactNode
  }[]
  startContent?: React.ReactNode
  endContent?: React.ReactNode
  className?: string
  hideArrow?: boolean
  hideStartContentItem?: boolean
  popoverContentClassName?: string
  placement?: 'bottom-start' | 'bottom-end'
  defaultSelectedOptionIndex?: number
  selectedOption?: string
  onOptionChange?: (option: string) => void
  size?: 'sm' | 'md' | 'lg'
  showHighlight?: boolean
  classNameBtn?: string
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [internalSelectedOption, setInternalSelectedOption] = useState<string>('')

  // Use external selected option if provided, otherwise use internal state
  const selectedOption =
    externalSelectedOption !== undefined ? externalSelectedOption : internalSelectedOption

  useEffect(() => {
    if (defaultSelectedOptionIndex !== undefined && selectedOption === '') {
      const newOption = options[defaultSelectedOptionIndex].label

      setInternalSelectedOption(newOption)
      onOptionChange?.(newOption)
    }
  }, [defaultSelectedOptionIndex, options, selectedOption, onOptionChange])

  const handleOptionSelect = (option: string) => {
    if (externalSelectedOption !== undefined) {
      // Controlled mode - notify parent
      onOptionChange?.(option)
    } else {
      // Uncontrolled mode - update internal state
      setInternalSelectedOption(option)
    }
    setIsOpen(false)
  }

  return (
    <Popover
      isOpen={isOpen}
      placement={placement}
      classNames={{
        content: `bg-white dark:bg-[#343537] border border-gray-300 dark:border-[#707070] w-[185px] text-gray-900 dark:text-gray-300 transition-all duration-500 ease-in-out `,
      }}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger>
        <Button
          aria-label="Select date range"
          className={cn(classNameBtn)}
          size={size}
          startContent={startContent}
          variant="flat"
          endContent={
            hideArrow ? null : endContent ? (
              endContent
            ) : (
              <img alt="down-arrow" className="w-5 h-5" src="/assets/svg/down-arrow.svg" />
            )
          }
        >
          {selectedOption || title}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className={cn(
          'bg-white dark:bg-[#343537] overflow-hidden px-0 py-2 text-gray-900 dark:text-gray-300',
          popoverContentClassName,
        )}
      >
        {options.map((option, idx) => (
          <div key={idx} className="w-full">
            <Button
              disableAnimation
              fullWidth
              size="sm"
              className={cn(
                'justify-start text-left text-gray-900 rounded-none dark:text-gray-300 !bg-transparent hover:!bg-gray-100 dark:hover:!bg-gray-700',
                // Active filter styling - distinct visual indicator for selected option
                selectedOption === option.label &&
                  showHighlight && [
                    '!bg-blue-50 dark:!bg-blue-900/20',
                    'text-blue-700 dark:text-blue-300',
                    'font-medium',
                    'shadow-sm',
                  ],
              )}
              startContent={
                !hideStartContentItem
                  ? option.icon || (
                      <FileTypeIcon
                        filename={option.label}
                        mediaType={option.mediaType}
                        mimeType={option.mimeType}
                        size={24}
                      />
                    )
                  : null
              }
              onPress={() => {
                handleOptionSelect(option.label)
                option.onSelect()
              }}
            >
              {option.label}
            </Button>
            {option.separator && <div className="w-full h-[1px] bg-gray-200 dark:bg-white my-1" />}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  )
}

export default Dropdown
